import { useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { api } from "./api";
import type { Task, TaskList } from "./types";

export type TaskSummary = {
  today: number;
  total: number;
  open: number;
  overdue: number;
  review: number;
  completed: number;
  mine: number;
  mine_by_board: Record<string, number>;
  delegated_review: number;
  columns: Record<string, { active: number; archived: number; total: number }>;
  assignees: Record<string, { open: number; overdue: number }>;
};
export function useTaskSummary(path = "") {
  return useQuery({
    queryKey: ["task-summary", path],
    queryFn: () => api<TaskSummary>("/tasks/summary" + path),
  });
}

// Each page remains a separate cache entry; fetching more never reloads earlier pages.
export function useTaskPages(path: string, columns?: string[], enabled = true) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const buckets = columns ?? [""];
  const pages = buckets.flatMap((column) =>
    Array.from({ length: counts[path + column] || 1 }, (_, page) => ({
      column,
      page,
    })),
  );
  const queries = useQueries({
    queries: pages.map(({ column, page }) => ({
      queryKey: ["tasks", "pages", path, column, page],
      queryFn: () =>
        api<TaskList>(
          `${path}&limit=50&offset=${page * 50}${column ? "&column_id=" + column : ""}`,
        ),
      enabled,
    })),
  });
  const data = queries.flatMap((q) => q.data?.items || []) as Task[];
  const totals: Record<string, number> = {};
  const more: Record<string, boolean> = {};
  pages.forEach(({ column }, index) => {
    if (queries[index].data) {
      totals[column] = queries[index].data!.total;
      more[column] = queries[index].data!.next_offset !== null;
    }
  });
  return {
    data: Array.from(new Map(data.map((t) => [t.id, t])).values()),
    totals,
    hasMore: (column = "") => !!more[column],
    loadMore: (column = "") =>
      setCounts((v) => ({
        ...v,
        [path + column]: (v[path + column] || 1) + 1,
      })),
    isPending: enabled && queries.some((q) => q.isPending),
    isFetching: queries.some((q) => q.isFetching),
    error: queries.find((q) => q.error)?.error,
    refetch: () => Promise.all(queries.map((q) => q.refetch())),
  };
}
